<template>
  <div :class="$style.wrapper">
    <label v-if="label" :for="INPUT_ID" :class="$style.label">{{ label }}</label>
    <div :class="$style.slotsWrapper" data-test="verification-code-slots" @click="focusInput">
      <div
        v-for="(_, index) in codeLength"
        :key="index"
        :class="[
          $style.slot,
          {
            [$style.slotActive]: isSlotActive(index),
            [$style.slotFilled]: Boolean(codeDigits[index]),
          },
        ]"
        :data-test="`verification-code-slot-${index}`"
        aria-hidden="true"
      >
        <span :class="$style.slotDigit">{{ codeDigits[index] }}</span>
      </div>

      <input
        :id="INPUT_ID"
        ref="verificationCodeInput"
        v-model="verificationCode"
        type="text"
        inputmode="numeric"
        name="verification-code"
        pattern="[0-9]*"
        :maxlength="codeLength"
        autocomplete="one-time-code"
        :class="$style.hiddenInput"
        @input="handleInput"
        @keydown="handleKeydown"
        @paste="handlePaste"
        @focus="isFocused = true"
        @blur="isFocused = false"
      />
    </div>
    <div v-if="!!$slots.errorMessages" :class="[$style.message, $style.errorMessages]">
      <slot name="errorMessages" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';

interface Props {
  label?: string;
  code?: string | null;
  codeLength?: number;
}

const props = withDefaults(defineProps<Props>(), {
  label: '',
  code: null,
  codeLength: 4,
});

const emit = defineEmits<{
  'update:code': [value: string];
  submit: [];
}>();

const INPUT_ID = 'verification-code-input';
const verificationCodeInput = ref<HTMLInputElement>();
const verificationCode = ref(sanitizeCode(props.code ?? ''));
const isFocused = ref(false);

const codeDigits = computed((): string[] =>
  Array.from({ length: props.codeLength }, (_, index) => verificationCode.value[index] ?? ''),
);

function sanitizeCode(value: string): string {
  return value.replace(/\D/g, '').slice(0, props.codeLength);
}

const updateCode = (value: string) => {
  verificationCode.value = sanitizeCode(value);
  emit('update:code', verificationCode.value);
};

const focusInput = () => {
  verificationCodeInput.value?.focus();
};

const isSlotActive = (index: number): boolean => {
  if (!isFocused.value) {
    return false;
  }

  const activeIndex = Math.min(verificationCode.value.length, props.codeLength - 1);
  return index === activeIndex;
};

onMounted(() => {
  if (!props.code?.length) {
    focusInput();
  }
});

const handleInput = () => {
  updateCode(verificationCode.value);
};

const handlePaste = (event: ClipboardEvent) => {
  event.preventDefault();

  const pastedText = event.clipboardData?.getData('text') ?? '';
  updateCode(pastedText);
};

const ALLOWED_KEYS = new Set([
  'Backspace',
  'Delete',
  'Tab',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Home',
  'End',
]);

const validateDigit = (event: KeyboardEvent) => {
  if (event.ctrlKey || event.metaKey || event.altKey) {
    return;
  }

  const { key } = event;
  if (ALLOWED_KEYS.has(key) || /^\d$/.test(key)) {
    return;
  }

  event.preventDefault();
};

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Enter') {
    event.preventDefault();

    if (verificationCode.value.length === props.codeLength) {
      emit('submit');
    }

    return;
  }

  validateDigit(event);
};
</script>

<style lang="scss" module>
@use '@/assets/variables' as *;

$slot-width: 3.25rem;
$slot-height: 4.25rem;

.wrapper {
  position: relative;
  box-sizing: border-box;
  display: block;

  * {
    box-sizing: border-box;
  }
}

.label {
  cursor: auto;
  display: block;
  margin-bottom: $space-50;
  text-align: center;

  @include label;
}

.slotsWrapper {
  position: relative;
  display: flex;
  gap: $space-50;
  justify-content: center;
  width: 100%;
  min-height: $slot-height;
}

.slot {
  display: flex;
  align-items: center;
  justify-content: center;
  width: $slot-width;
  height: $slot-height;
  background: $color-white;
  border: 2px solid $color-grey-50;
  border-radius: $border-radius;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.slotActive {
  border-color: var(--base-color-link);
  box-shadow: 0 0 0 1px var(--base-color-link);
}

.slotFilled {
  border-color: $color-grey-100;
}

.slotDigit {
  @include heading-3;

  line-height: 1;
  color: $color-grey-120;
}

.hiddenInput {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: text;
}

.message {
  margin-top: $space-25;

  @include body-3;

  > * {
    display: block;
    margin: 0 0 $space-25 0;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.errorMessages {
  color: var(--validation-error-color);
}
</style>
